import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Typography, Button, Modal, IconButton, CircularProgress, Autocomplete, TextField, Chip } from '@mui/material';
import { UserService } from '../api/services/userService';

export default function EditRecipientsModal({ open, onClose, onSave, editingTrigger = null, loading = false }) {
    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [emailInput, setEmailInput] = useState('');
    const [fetchingRoles, setFetchingRoles] = useState(false);

    useEffect(() => {
        if (open) {
            fetchRoles();
            setEmailInput('');
            if (editingTrigger) {
                setSelectedRoles(editingTrigger.recipientRoles || []);
                setSelectedEmails(editingTrigger.recipientEmails || []);
            } else {
                setSelectedRoles([]);
                setSelectedEmails([]);
            }
        }
    }, [open, editingTrigger]);

    const fetchRoles = async () => {
        setFetchingRoles(true);
        try {
            const data = await UserService.getRoles();
            setRoles(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to fetch roles:', error);
            // Fallback generic roles in case API fails
            setRoles([
                { name: 'Super Admin' },
                { name: 'Admin' },
                { name: 'Manager' },
                { name: 'Front End Office Staff' },
                { name: 'Accountant' },
                { name: 'StoreKeeper' },
                { name: 'Housekeeping' }
            ]);
        } finally {
            setFetchingRoles(false);
        }
    };

    const handleAddEmail = (e) => {
        if (e.key === 'Enter' && emailInput.trim()) {
            e.preventDefault();
            const newEmail = emailInput.trim();
            // Basic email validation
            if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
                if (!selectedEmails.includes(newEmail)) {
                    setSelectedEmails([...selectedEmails, newEmail]);
                }
                setEmailInput('');
            }
        }
    };

    const handleRemoveEmail = (emailToRemove) => {
        setSelectedEmails(selectedEmails.filter(e => e !== emailToRemove));
    };

    const handleSubmit = () => {
        let finalEmails = [...selectedEmails];
        const pendingEmail = emailInput.trim();
        
        if (pendingEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pendingEmail)) {
            if (!finalEmails.includes(pendingEmail)) {
                finalEmails.push(pendingEmail);
            }
        }
        
        onSave({ recipientRoles: selectedRoles, recipientEmails: finalEmails });
    };

    return (
        <Modal open={open} onClose={loading ? null : onClose} disableEscapeKeyDown>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: 450 }, bgcolor: 'background.paper', borderRadius: 4, p: 4, boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                display: 'flex', flexDirection: 'column', gap: 3
            }}>
                <IconButton 
                    sx={{ position: 'absolute', top: 12, right: 12, color: 'text.secondary' }} 
                    onClick={onClose}
                    disabled={loading}
                >
                    <CloseIcon />
                </IconButton>

                <Box>
                    <Typography variant="h5" fontWeight="900" sx={{ mb: 1, color: '#1a1a1a' }}>
                        Edit Recipients
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        Select the roles that should receive notifications for <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main', bgcolor: 'primary.50', px: 1, py: 0.5, borderRadius: 1 }}>{editingTrigger?.eventName?.replace(/_/g, ' ')}</Box>.
                    </Typography>
                </Box>

                {fetchingRoles ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress size={30} />
                    </Box>
                ) : (
                    <Autocomplete
                        multiple
                        options={roles.map(r => r.name)}
                        value={selectedRoles}
                        onChange={(event, newValue) => {
                            setSelectedRoles(newValue);
                        }}
                        disabled={loading}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({ index });
                                return (
                                    <Chip
                                        key={key}
                                        label={option}
                                        {...tagProps}
                                        sx={{ 
                                            bgcolor: 'primary.50', 
                                            color: 'primary.main',
                                            fontWeight: 'bold',
                                            border: '1px solid',
                                            borderColor: 'primary.200',
                                            '& .MuiChip-deleteIcon': {
                                                color: 'primary.main',
                                                '&:hover': { color: 'primary.dark' }
                                            }
                                        }}
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Assigned Roles"
                                placeholder="Select roles..."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        )}
                        sx={{ width: '100%' }}
                    />
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                        fullWidth
                        label="Specific Email Addresses"
                        placeholder="Type an email and press Enter"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={handleAddEmail}
                        disabled={loading}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        helperText="Press Enter to add multiple email addresses."
                    />
                    {selectedEmails.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {selectedEmails.map((email) => (
                                <Chip
                                    key={email}
                                    label={email}
                                    onDelete={() => handleRemoveEmail(email)}
                                    disabled={loading}
                                    sx={{ 
                                        bgcolor: 'rgba(0,0,0,0.04)', 
                                        color: 'text.primary',
                                        border: '1px solid',
                                        borderColor: 'rgba(0,0,0,0.12)'
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Button 
                        fullWidth 
                        variant="outlined" 
                        color="error" 
                        onClick={onClose} 
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        fullWidth 
                        variant="contained" 
                        onClick={handleSubmit} 
                        disabled={loading || fetchingRoles}
                        startIcon={loading && <CircularProgress size={20} color="inherit" />}
                    >
                        {loading ? 'Saving...' : 'Save Recipients'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
